import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { InquiryFormData, BookingFormData } from '@/types';
import { trackFormSubmission } from '@/lib/analytics';
import { IS_DEV } from '@/config/analytics';

const SuccessPage = () => {
  const [searchParams] = useSearchParams();
  const [paymentType, setPaymentType] = useState<'consultation' | 'booking' | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState<InquiryFormData | BookingFormData>({
    name: '',
    email: '',
    contactMethod: 'WhatsApp',
    contactNumber: '',
    nationality: '',
    symptoms: '',
  });

  useEffect(() => {
    const type = searchParams.get('type');
    if (type === 'consultation' || type === 'booking') {
      setPaymentType(type);
      // Track form started when payment type is determined
      trackFormSubmission(type, 'started');
      
      if (IS_DEV) {
        console.log('[Analytics] Form started:', type);
      }
    }
  }, [searchParams]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev: InquiryFormData | BookingFormData) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch('/api/submit-form', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          paymentType,
        }),
      });

      if (response.ok) {
        setSubmitted(true);
        // Track successful form submission
        trackFormSubmission(paymentType!, 'completed');
        
        if (IS_DEV) {
          console.log('[Analytics] Form completed:', paymentType);
        }
      } else {
        throw new Error('Failed to submit form');
      }
    } catch (error) {
      // Track form submission error
      trackFormSubmission(paymentType!, 'error', error instanceof Error ? error.message : 'unknown');
      alert('Error submitting form. Please try again.');
      
      if (IS_DEV) {
        console.log('[Analytics] Form error:', paymentType, error);
      }
    } finally {
      setLoading(false);
    }
  };

  if (!paymentType) {
    return (
      <div className="container-custom">
        <div className="max-w-2xl mx-auto text-center py-20">
          <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <h1 className="text-3xl font-bold mb-4">Invalid Payment Type</h1>
          <p className="text-gray-600 mb-8">
            We couldn't determine the type of service you purchased.
          </p>
          <Link to="/services" className="btn-primary">
            Back to Services
          </Link>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="container-custom">
        <div className="max-w-2xl mx-auto text-center py-20">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold mb-4">Thank You!</h1>
          <p className="text-gray-600 mb-6">
            Your information has been submitted successfully. We'll be in touch within 24-48 hours.
          </p>
          <div className="bg-gray-50 rounded-lg p-6 mb-8">
            <h3 className="font-semibold mb-4">What happens next:</h3>
            <ul className="space-y-3 text-left">
              <li className="flex items-start">
                <div className="w-6 h-6 bg-primary-600 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                  <span className="text-xs font-bold text-white">1</span>
                </div>
                <span className="text-sm">You'll receive a confirmation email with your reference number</span>
              </li>
              <li className="flex items-start">
                <div className="w-6 h-6 bg-primary-600 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                  <span className="text-xs font-bold text-white">2</span>
                </div>
                <span className="text-sm">Our team will review your information and respond within 24-48 hours</span>
              </li>
              <li className="flex items-start">
                <div className="w-6 h-6 bg-primary-600 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                  <span className="text-xs font-bold text-white">3</span>
                </div>
                <span className="text-sm">We'll provide personalized recommendations or schedule your appointment</span>
              </li>
            </ul>
          </div>
          <Link to="/" className="btn-secondary">
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container-custom">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold mb-2">Payment Successful!</h1>
          <p className="text-gray-600">
            {paymentType === 'consultation' 
              ? 'You have purchased a consultation service. Please provide your details below.'
              : 'You have purchased a booking service. Please provide your details below.'}
          </p>
        </div>

        <div className="card p-8">
          <h2 className="text-2xl font-bold mb-6">
            {paymentType === 'consultation' ? 'Consultation Form' : 'Booking Form'}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  className="input-field"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  className="input-field"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="john@example.com"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contact Method *
                </label>
                <select
                  name="contactMethod"
                  required
                  className="input-field"
                  value={formData.contactMethod}
                  onChange={handleInputChange}
                >
                  <option value="WhatsApp">WhatsApp</option>
                  <option value="WeChat">WeChat</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {formData.contactMethod === 'WhatsApp' ? 'WhatsApp Number *' : 'WeChat ID *'}
                </label>
                <input
                  type="text"
                  name="contactNumber"
                  required
                  className="input-field"
                  value={formData.contactNumber}
                  onChange={handleInputChange}
                  placeholder={formData.contactMethod === 'WhatsApp' ? '+1 234 567 8900' : 'your-wechat-id'}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nationality *
                </label>
                <input
                  type="text"
                  name="nationality"
                  required
                  className="input-field"
                  value={formData.nationality}
                  onChange={handleInputChange}
                  placeholder="United States"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Preferred City (Optional)
                </label>
                <select
                  name="preferredCity"
                  className="input-field"
                  value={(formData as BookingFormData).preferredCity || ''}
                  onChange={handleInputChange}
                >
                  <option value="">Select city</option>
                  <option value="Beijing">Beijing</option>
                  <option value="Shanghai">Shanghai</option>
                  <option value="Guangzhou">Guangzhou</option>
                  <option value="Shenzhen">Shenzhen</option>
                  <option value="Chengdu">Chengdu</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Symptoms / Medical Condition *
              </label>
              <textarea
                name="symptoms"
                required
                rows={4}
                className="input-field"
                value={formData.symptoms}
                onChange={handleInputChange}
                placeholder="Please describe your symptoms or medical condition in detail..."
              />
            </div>

            {paymentType === 'booking' && (
              <>
                <div className="border-t border-gray-200 pt-6 mt-6">
                  <h3 className="text-lg font-semibold mb-4">Additional Information</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Preferred Date *
                    </label>
                    <input
                      type="date"
                      name="preferredDate"
                      required
                      className="input-field"
                      value={(formData as BookingFormData).preferredDate || ''}
                      onChange={handleInputChange}
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Preferred Time Slot (Optional)
                    </label>
                    <select
                      name="preferredTimeSlot"
                      className="input-field"
                      value={(formData as BookingFormData).preferredTimeSlot || ''}
                      onChange={handleInputChange}
                    >
                      <option value="">Any time</option>
                      <option value="morning">Morning (8am - 12pm)</option>
                      <option value="afternoon">Afternoon (12pm - 5pm)</option>
                      <option value="evening">Evening (5pm - 8pm)</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Passport Scan (Optional)
                  </label>
                  <input
                    type="file"
                    name="passportUpload"
                    accept="image/*,.pdf"
                    className="input-field"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Upload a scan of your passport data page (JPEG, PNG, or PDF, max 5MB)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Medical Records (Optional)
                  </label>
                  <input
                    type="file"
                    name="medicalRecordsUpload"
                    accept=".pdf,.jpg,.jpeg,.png"
                    multiple
                    className="input-field"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Upload relevant medical records, test results, or doctor's notes (PDF or images, max 10MB each)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Notes (Optional)
                  </label>
                  <textarea
                    name="additionalNotes"
                    rows={3}
                    className="input-field"
                    value={(formData as BookingFormData).additionalNotes || ''}
                    onChange={handleInputChange}
                    placeholder="Any additional information you'd like to share..."
                  />
                </div>
              </>
            )}

            <div className="border-t border-gray-200 pt-6">
              <div className="flex items-start mb-4">
                <input
                  type="checkbox"
                  required
                  id="consent"
                  className="mt-1 mr-3"
                />
                <label htmlFor="consent" className="text-sm text-gray-600">
                  I understand that this website provides information services and administrative assistance only. 
                  All medical services will be provided by the hospitals. I agree to the{' '}
                  <Link to="/privacy" className="text-primary-600 hover:underline">
                    Privacy Policy
                  </Link>{' '}
                  and{' '}
                  <Link to="/disclaimer" className="text-primary-600 hover:underline">
                    Disclaimer
                  </Link>
                  .
                </label>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <Link to="/services" className="text-sm text-gray-600 hover:text-gray-900">
                Cancel
              </Link>
              <button
                type="submit"
                disabled={loading}
                className="btn-primary"
              >
                {loading ? 'Submitting...' : 'Submit Form'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SuccessPage;
